/**
 * EGLO Product Downloads - Custom Element
 *
 * Loads the download center documents (same EGLO API as download-center.js) for one article number
 * and renders them inside the PDP "Downloads" accordion. Files on hosts the Storyblok Sync App Proxy
 * supports download as attachment; other hosts open in a new tab.
 * The API is only called when the accordion is opened (EGLO-131). No documents or a failed request
 * show a message inside the accordion.
 */

if (!customElements.get('product-downloads')) {
  /** Hosts the App Proxy streams as attachment (storyblok-sync app/lib/download-proxy.server.js). */
  const PROXY_HOSTS = new Set(['eglo.contentdeliveryhub.net', 'tools.eglo.com']);

  const ICONS = {
    pdf: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
    image: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>',
    file: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
    download: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>',
  };

  customElements.define(
    'product-downloads',
    class ProductDownloads extends HTMLElement {
      connectedCallback() {
        this._statusEl = this.querySelector('.product-downloads__status');
        this._listEl = this.querySelector('.product-downloads__list');
        this._detailsEl = this.querySelector('details');

        if (!this._listEl || !this._detailsEl || !this._isConfigured()) return;

        // Call the API only when the accordion is opened, never on page load or scroll: bots render the whole
        // page, so a scroll trigger requests the documents from Lobster on almost every bot visit (EGLO-131).
        this._onToggle = () => {
          if (this._detailsEl.open) this._load();
        };
        this._detailsEl.addEventListener('toggle', this._onToggle);

        // Opened before this script ran (slow connection)
        if (this._detailsEl.open) this._load();
      }

      disconnectedCallback() {
        this._detailsEl?.removeEventListener('toggle', this._onToggle);
      }

      _isConfigured() {
        const { sku, apiBaseUrl, apiUser, apiPassword } = this.dataset;
        return Boolean(sku && apiBaseUrl && apiUser && apiPassword);
      }

      async _load() {
        if (this._loadStarted) return;
        this._loadStarted = true;
        this._detailsEl.removeEventListener('toggle', this._onToggle);

        const { sku, apiBaseUrl, apiShopId, apiUser, apiPassword, locale } = this.dataset;
        const apiLocale = (locale || 'de').toUpperCase();
        const apiUrl = `${apiBaseUrl.replace(/\/$/, '')}/${encodeURIComponent(apiShopId || 'eglo-prod')}/${encodeURIComponent(sku)}/${encodeURIComponent(apiLocale)}`;

        let documents;
        try {
          const response = await fetch(apiUrl, {
            headers: {
              Authorization: 'Basic ' + btoa(`${apiUser}:${apiPassword}`),
              Accept: 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const rawData = await response.json();
          const mediaLinks = Array.isArray(rawData) ? rawData : (rawData?.MediaLinks ?? []);
          documents = mediaLinks.filter((item) => item?.MediaURL);
        } catch {
          this._showMessage(this.dataset.textError);
          return;
        }

        if (documents.length === 0) {
          this._showMessage(this.dataset.textEmpty);
          return;
        }

        this._listEl.replaceChildren(...documents.map((doc) => this._createItem(doc)));
        this._listEl.hidden = false;
        this._statusEl?.setAttribute('hidden', '');
      }

      /** The customer just opened the accordion, so explain the empty list instead of hiding it. */
      _showMessage(message) {
        if (this._statusEl) this._statusEl.textContent = message || '';
      }

      _createItem(doc) {
        const mediaUrl = doc.MediaURL;
        const filename = this._resolveFilename(doc.Filename, mediaUrl);

        const link = document.createElement('a');
        link.className = 'product-downloads__link';

        if (this._canUseProxy(mediaUrl)) {
          link.href = this._buildProxyUrl(mediaUrl, filename);
          link.download = filename;
        } else {
          link.href = mediaUrl;
          link.target = '_blank';
          link.rel = 'noopener';
        }

        const icon = document.createElement('span');
        icon.className = 'product-downloads__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = ICONS[this._getFileKind(filename)];

        const type = document.createElement('span');
        type.className = 'product-downloads__type';
        type.textContent = this._getTypeLabel(doc);

        // Web pages (e.g. EPREL product page) have no filename: show the domain instead of a URL fragment
        const name = document.createElement('span');
        name.className = 'product-downloads__filename';
        name.textContent = doc.Filename || this._getHost(mediaUrl) || filename;

        const text = document.createElement('span');
        text.className = 'product-downloads__text';
        text.append(type, name);

        const actionLabel = document.createElement('span');
        actionLabel.className = 'sr-only';
        actionLabel.textContent = this.dataset.textDownload || 'Download';

        const action = document.createElement('span');
        action.className = 'product-downloads__action';
        action.innerHTML = ICONS.download;
        action.append(actionLabel);

        link.append(icon, text, action);

        const item = document.createElement('li');
        item.className = 'product-downloads__item';
        item.append(link);

        return item;
      }

      _getHost(mediaUrl) {
        try {
          return new URL(mediaUrl).hostname;
        } catch {
          return '';
        }
      }

      _canUseProxy(mediaUrl) {
        return Boolean(this.dataset.downloadProxy) && PROXY_HOSTS.has(this._getHost(mediaUrl));
      }

      _buildProxyUrl(mediaUrl, filename) {
        const params = new URLSearchParams({ url: mediaUrl, filename: filename || 'download' });
        return `${this.dataset.downloadProxy.replace(/\/$/, '')}?${params.toString()}`;
      }

      _resolveFilename(filename, url) {
        if (filename) return filename;

        try {
          const lastSegment = new URL(url).pathname.split('/').pop();
          if (lastSegment) return decodeURIComponent(lastSegment);
        } catch {
          /* ignore */
        }

        return 'download';
      }

      _getFileKind(filename) {
        const ext = filename.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return 'pdf';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'tif', 'tiff'].includes(ext)) return 'image';
        return 'file';
      }

      /** Same label logic as the download center: localized text unless the API still returns "TODO". */
      _getTypeLabel(doc) {
        const localized = doc.MediaTypeDescriptionLocalized;
        if (localized && localized !== 'TODO') return localized;
        return doc.MediaTypeDescriptionEN || doc.MediaType || '';
      }
    },
  );
}
